class UIViewport extends HTMLElement {
  constructor() {
    super();

    this.minDesktopWidth = DESKTOP_WIDTH || 1023;
    this.observer = null;
    this.debounceTimeout = null;
    this.isVisible = null;
    this.viewportWrapper = null;
    this.debounceUpdate = this.debounce(this.updateVisibility.bind(this), 200);
    this.resizeUpdate = this.debounce(this.handleResize.bind(this), 200);
    this.customElementStyles = null;
    this.isScrollingUp = false;
    this.wayPassedElement = false;
    this.resizeObserver = null;
    this.isMobileView = false;
    this.currentParentElement = null;
    this.isSticky = false;
    this.sectionOptionSticky = 'false';
    this.windowHeight = 0;
    this.bodyWidth = document?.body?.offsetWidth;
  }

  connectedCallback() {
    this.windowHeight =
      window?.innerHeight ||
      document?.documentElement.clientHeight ||
      body?.clientHeight;

    this.dataId = this.getAttribute('data-id');
    this.isMobileView = this.bodyWidth < this.minDesktopWidth;
    this.sectionOptionSticky = this.getAttribute('data-sticky') == 'true';

    this.viewportWrapper = this.querySelector('.viewport-wrapper');

    this.currentParentElement = document.getElementById(
      `${this.getAttribute('data-parent-id')}`
    );

    // Create Intersection Observer when element is added to DOM
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this), // bind this to handleIntersection so we will listen to this custom element
      {
        root: null, // viewport
        rootMargin: '0px', // no margin, rem will not work on rootMargin
        threshold: [0, 1.0], // trigger at 0% and 100% visibility
      }
    );

    // Set up ResizeObserver to watch body width
    if (!this.resizeObserver) {
      this.observeBodyWidth();
    }

    // get current element styling so it will be available globally
    this.customElementStyles = window.getComputedStyle(this);

    // Start observing this element
    this.observer.observe(this);
    //add scroll event
    window.addEventListener('scroll', this.handleScrollEvent.bind(this));
  }

  // cleanups
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.debounceUpdate) {
      this.debounceUpdate = null;
    }

    if (this.resizeUpdate) {
      this.resizeUpdate = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.viewportWrapper = null;

    //remove scroll event
    window.removeEventListener('scroll', this.handleScrollEvent.bind(this));
  }

  handleScrollEvent() {
    const currentScroll = window.scrollY;
    const relatedPosition = this.getBoundingClientRect().top - currentScroll;
    this.isScrollingUp = currentScroll < this._lastScrollPosition;
    this._lastScrollPosition = currentScroll;
    this.wayPassedElement = false;

    if (this.isSticky) {
      this.wayPassedElement =
        currentScroll <= this.windowHeight - relatedPosition;
    }
  }

  handleIntersection(entries) {
    // Use debounced function instead of direct update
    entries.forEach((entry) => {
      this.debounceUpdate(entry);
    });
  }

  handleResize() {
    this.bodyWidth = document?.body?.offsetWidth;
    this.isMobileView = this.bodyWidth < this.minDesktopWidth;
    if (this.viewportWrapper) {
      this.viewportWrapper.style.maxWidth = this.customElementStyles.width || 0;
    }
    this.classList.remove('opacity-0');
    this.handleIsSticky();
  }

  observeBodyWidth() {
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeUpdate(this);
    });

    // Start observing the body
    this.resizeObserver.observe(document.body);
  }

  handleIsSticky(entry) {
    const parentOffsetHeight = this.currentParentElement.offsetHeight || 0;
    this.isSticky = this.isMobileView || parentOffsetHeight > this.windowHeight;
    let isVisible = entry?.isIntersecting;

    if (this.isSticky && this.sectionOptionSticky) {
      if (this.wayPassedElement) {
        isVisible = true;
      }

      this.setAttribute('aria-hidden', !isVisible);
    }
  }

  updateVisibility(entry) {
    let isVisible = entry?.isIntersecting;

    if (this.wayPassedElement) {
      isVisible = true;
    }

    this.handleIsSticky(entry);
    this.dispatchEvent(
      new CustomEvent('visibilityChange', {
        detail: {
          isVisible,
          intersectionRatio: entry.intersectionRatio,
          timestamp: Date.now(),
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  static observedAttributes = ['aria-hidden'];
}

customElements.define('ui-viewport', UIViewport);
